import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tokenKey, webTokenKey } from '../utils/authHelpers';
import { sidKey } from '../utils/userProfileContextHelpers';

const getWebToken = () => localStorage.getItem(webTokenKey);
const getSessionId = () => localStorage.getItem(sidKey);
const getApiToken = () => localStorage.getItem(tokenKey);

/**
 * Identifies an activity session, including credential changes in other tabs.
 * @returns A stable session identity until authentication or credentials change.
 */
export const useActivitySession = () => {
    const { isAuthenticated, token } = useAuth();
    const [, refresh] = useState(0);
    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (
                event.key === null ||
                event.key === webTokenKey ||
                event.key === sidKey ||
                event.key === tokenKey
            ) {
                refresh(version => version + 1);
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);
    const webToken = getWebToken();
    const sid = getSessionId();
    const apiToken = getApiToken();

    return useMemo(
        () => ({
            isAuthenticated: Boolean(
                isAuthenticated &&
                    webToken &&
                    sid &&
                    token &&
                    token === apiToken
            ),
            webToken,
            sid,
            isCurrent: () =>
                webToken === getWebToken() &&
                sid === getSessionId() &&
                apiToken === getApiToken(),
        }),
        [isAuthenticated, token, webToken, sid, apiToken]
    );
};
